import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, type HorecaCountDto } from "@finora/api-client";
import { Button, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";
import { ModuleGateNotice } from "../../components/ModuleGateNotice";

const inputCls = "h-9 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none";
const fmt = (n: number) => n.toLocaleString("sr-RS", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const qty = (n: number) => n.toLocaleString("sr-RS", { maximumFractionDigits: 3 });
const STATUS: Record<string, string> = { OPEN: "u toku", APPLIED: "primenjen", CANCELLED: "otkazan" };

function errMsg(e: unknown) {
  if (e instanceof ApiError) {
    const m = e.response.message;
    return Array.isArray(m) ? m.join(" ") : m;
  }
  return "Radnja nije uspela.";
}

export function HorecaPopisPage() {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [warehouse, setWarehouse] = useState("");
  const [onlyIng, setOnlyIng] = useState(false);
  const list = useQuery({ queryKey: ["horeca-counts"], queryFn: () => api.listHorecaCounts(), retry: false });
  const whs = useQuery({ queryKey: ["horeca-warehouses"], queryFn: () => api.listWarehouses(), retry: false });
  const create = useMutation({
    mutationFn: () => api.createHorecaCount({ warehouse_id: warehouse || (whs.data?.[0]?.id as string), only_ingredients: onlyIng }),
    onSuccess: (c) => { setErr(null); qc.invalidateQueries({ queryKey: ["horeca-counts"] }); setOpenId(c.id); },
    onError: (e) => setErr(errMsg(e)),
  });

  if (list.isError) {
    if (list.error instanceof ApiError && list.error.response.errorCode === "MODULE_NOT_AVAILABLE") return <ModuleGateNotice error={list.error} moduleKey="horeca" />;
    return <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">Učitavanje nije uspelo.</p>;
  }
  if (openId) return <CountDetail id={openId} onBack={() => { setOpenId(null); qc.invalidateQueries({ queryKey: ["horeca-counts"] }); }} />;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Popis zaliha</h1>
        <p className="text-sm text-slate-500">Otvorite popis, upišite prebrojane količine, pa primenite razliku na stanje magacina.</p>
      </div>
      {err && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-surface p-3 shadow-subtle">
        <label className="text-xs text-slate-500">Magacin
          <select className={inputCls + " block w-56"} value={warehouse} onChange={(e) => setWarehouse(e.target.value)}>
            {(whs.data ?? []).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={onlyIng} onChange={(e) => setOnlyIng(e.target.checked)} /> Samo namirnice</label>
        <Button disabled={create.isPending || !whs.data?.length} onClick={() => create.mutate()}>Novi popis</Button>
      </div>
      {list.isLoading && <Skeleton className="h-20 w-full" />}
      {list.data?.counts.length === 0 && <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-500">Još nema popisa.</p>}
      {list.data?.counts.map((c) => (
        <button key={c.id} onClick={() => setOpenId(c.id)} className="flex w-full items-center justify-between rounded-xl border border-border bg-surface p-3 text-left shadow-subtle hover:bg-slate-50">
          <span className="text-sm font-semibold">{c.name} <span className="font-normal text-slate-500">· {c.warehouse_name}</span></span>
          <span className="text-xs text-slate-500">{new Date(c.created_at).toLocaleDateString("sr-RS")} · {STATUS[c.status]}</span>
        </button>
      ))}
    </div>
  );
}

function CountDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const [vals, setVals] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState("");
  const q = useQuery({ queryKey: ["horeca-count", id], queryFn: () => api.getHorecaCount(id), retry: false });
  useEffect(() => {
    if (q.data?.lines) setVals(Object.fromEntries(q.data.lines.map((l) => [l.product_id, l.counted === null ? "" : String(l.counted)])));
  }, [q.data]);
  const after = (c: HorecaCountDto) => { setErr(null); qc.setQueryData(["horeca-count", id], c); qc.invalidateQueries({ queryKey: ["horeca-counts"] }); };
  const save = useMutation({
    mutationFn: () => {
      const changed = (q.data?.lines ?? []).filter((l) => {
        const v = (vals[l.product_id] ?? "").trim();
        return v === "" ? l.counted !== null : Number(v.replace(",", ".")) !== l.counted;
      });
      return api.saveHorecaCountLines(id, changed.map((l) => {
        const v = (vals[l.product_id] ?? "").trim();
        return { product_id: l.product_id, counted_qty: v === "" ? null : Number(v.replace(",", ".")) };
      }));
    },
    onSuccess: after,
    onError: (e) => setErr(errMsg(e)),
  });
  const apply = useMutation({ mutationFn: async () => { await save.mutateAsync(); return api.applyHorecaCount(id); }, onSuccess: after, onError: (e) => setErr(errMsg(e)) });
  const cancel = useMutation({ mutationFn: () => api.cancelHorecaCount(id), onSuccess: after, onError: (e) => setErr(errMsg(e)) });

  if (!q.data?.lines) return <Skeleton className="h-40 w-full" />;
  const c = q.data;
  const open = c.status === "OPEN";
  const rows = c.lines!.filter((l) => l.name.toLowerCase().includes(filter.toLowerCase()));
  return (
    <div className="print-area space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <button className="mb-1 text-sm text-brand print:hidden" onClick={onBack}>← Svi popisi</button>
          <h1 className="text-xl font-semibold text-slate-900">{c.name}</h1>
          <p className="text-sm text-slate-500">{c.warehouse_name} · {STATUS[c.status]} · prebrojano {c.summary?.counted}/{c.summary?.total} · razlika {fmt(c.summary?.value_difference ?? 0)} din (po nabavnoj ceni)</p>
        </div>
        <button className="rounded-lg border border-border px-3 py-2 text-sm hover:bg-slate-50 print:hidden" onClick={() => window.print()}>Štampaj</button>
      </div>
      {err && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 print:hidden">{err}</p>}
      <div className="flex flex-wrap items-center gap-2 print:hidden">
        <input className={inputCls + " w-60"} placeholder="Pretraga artikla" value={filter} onChange={(e) => setFilter(e.target.value)} />
        {open && (
          <>
            <Button disabled={save.isPending} onClick={() => save.mutate()}>Sačuvaj unos</Button>
            <Button disabled={apply.isPending} onClick={() => window.confirm("Primeniti popis na stanje magacina? Ovo se ne može poništiti.") && apply.mutate()}>Primeni na stanje</Button>
            <button className="rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50" onClick={() => window.confirm("Otkazati popis?") && cancel.mutate()}>Otkaži popis</button>
          </>
        )}
      </div>
      <div className="overflow-x-auto rounded-xl border border-border bg-surface shadow-subtle">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs text-slate-500">
              <th className="px-3 py-2 font-medium">Artikal</th>
              <th className="px-3 py-2 text-right font-medium">Očekivano</th>
              <th className="px-3 py-2 text-right font-medium">Prebrojano</th>
              <th className="px-3 py-2 text-right font-medium">Razlika</th>
              <th className="px-3 py-2 text-right font-medium">Vrednost</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((l) => {
              const v = (vals[l.product_id] ?? "").trim();
              const n = v === "" ? null : Number(v.replace(",", "."));
              const diff = n === null || Number.isNaN(n) ? null : Math.round((n - l.expected) * 10000) / 10000;
              return (
                <tr key={l.product_id} className="border-b border-border last:border-0">
                  <td className="px-3 py-1.5">{l.name} <span className="text-xs text-slate-400">{l.unit}</span></td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{qty(l.expected)}</td>
                  <td className="px-3 py-1.5 text-right">
                    {open ? (
                      <input className={inputCls + " w-24 text-right print:hidden"} inputMode="decimal" value={vals[l.product_id] ?? ""} onChange={(e) => setVals({ ...vals, [l.product_id]: e.target.value })} />
                    ) : (
                      <span className="tabular-nums">{l.counted === null ? "—" : qty(l.counted)}</span>
                    )}
                    {open && <span className="hidden tabular-nums print:inline">{v || "—"}</span>}
                  </td>
                  <td className={"px-3 py-1.5 text-right tabular-nums " + (diff ? (diff < 0 ? "text-red-600" : "text-emerald-700") : "")}>{diff === null ? "—" : qty(diff)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{l.value === null || !open && l.difference === null ? "—" : fmt(l.value)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
